import { useState } from 'react';
import { Box, Heading, Text, VStack, HStack, SimpleGrid, FormControl, FormLabel, Input, Textarea, Select, Button, Icon, Link as ChakraLink, useToast } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faHeadset, faBuilding } from '@fortawesome/free-solid-svg-icons';
import MarketingLayout from '../components/marketing/MarketingLayout';

// NOTE: This form is UI-only for now. Delivery (POST /api/contact + email)
// is intentionally not wired yet — to be added later.
const SUPPORT_EMAIL = 'support@statfalcon.com';

const initialForm = { name: '', email: '', subject: 'General', message: '' };

const channels = [
  { icon: faEnvelope, title: 'Email us', text: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
  { icon: faHeadset, title: 'Support', text: 'Priority support on paid plans' },
  { icon: faBuilding, title: 'Sales', text: 'Volume & agency pricing available' },
];

export const ContactPage = () => {
  const toast = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // TODO: POST form to /api/contact once the backend endpoint exists.
    setTimeout(() => {
      toast({ title: 'Thanks — we\'ll be in touch shortly.', status: 'success', duration: 4000 });
      setForm(initialForm);
      setSubmitting(false);
    }, 400);
  };

  return (
    <MarketingLayout title="Contact us" subtitle="Questions about plans, self-hosting, or a migration? Send us a note.">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={10}>
        {channels.map((c) => (
          <VStack key={c.title} align="start" spacing={2} p={6} bg="gray.50" borderRadius="2xl" border="1px solid" borderColor="gray.200">
            <Box w={10} h={10} bgGradient="linear(135deg, brand.500, accent.500)" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FontAwesomeIcon} icon={c.icon} color="white" />
            </Box>
            <Heading size="sm" color="gray.900">{c.title}</Heading>
            {c.href ? (
              <ChakraLink href={c.href} color="brand.600" fontWeight="600">{c.text}</ChakraLink>
            ) : (
              <Text color="gray.600" fontSize="sm">{c.text}</Text>
            )}
          </VStack>
        ))}
      </SimpleGrid>

      <Box as="form" onSubmit={handleSubmit} bg="gray.50" p={{ base: 6, md: 8 }} borderRadius="2xl" border="1px solid" borderColor="gray.200" shadow="sm">
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input name="name" value={form.name} onChange={handleChange} borderRadius="lg" placeholder="Your name" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input type="email" name="email" value={form.email} onChange={handleChange} borderRadius="lg" placeholder="you@company.com" />
            </FormControl>
          </SimpleGrid>
          <FormControl>
            <FormLabel>Topic</FormLabel>
            <Select name="subject" value={form.subject} onChange={handleChange} borderRadius="lg">
              <option value="General">General question</option>
              <option value="Sales">Sales &amp; pricing</option>
              <option value="Support">Support</option>
              <option value="Self-hosting">Self-hosting</option>
              <option value="Migration">Migration</option>
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Message</FormLabel>
            <Textarea name="message" value={form.message} onChange={handleChange} borderRadius="lg" rows={6} placeholder="How can we help?" />
          </FormControl>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Text color="gray.500" fontSize="sm">Prefer email? Reach us at <ChakraLink href={`mailto:${SUPPORT_EMAIL}`} color="brand.600">{SUPPORT_EMAIL}</ChakraLink></Text>
            <Button type="submit" colorScheme="brand" borderRadius="xl" px={8} isLoading={submitting}>Send message</Button>
          </HStack>
        </VStack>
      </Box>
    </MarketingLayout>
  );
};

export default ContactPage;
